import { useEffect, useMemo } from 'react';
import useOrg from 'src/hooks/useOrg';
import useOrgsForFoodRunCreation from './useOrgsForFoodRunCreation';

const useEditRecurringDonations = ({
  user,
  recurringPatternData = {},
  selectedDonor,
  ignoreDonorTeamId,
  ignoreNpoTeamId,
  isFoodRun,
  recipientMatchRequest,
  pickupLocation,
  existingRecipientTeamIds = [],
}) => {
  const recipientMatchRequestKey = JSON.stringify(recipientMatchRequest || {});
  const pickupLat = pickupLocation?.lat ?? null;
  const pickupLng = pickupLocation?.lng ?? null;
  const existingRecipientTeamIdsKey = existingRecipientTeamIds.join(',');
  const { getNearByNPOs, nearbyNPOs } = useOrg({
    history: null,
    user,
    isNonProfit: false,
  });

  useEffect(() => {
    getNearByNPOs({
      lat: user.lat,
      lng: user.lng,
      donationLat: pickupLat,
      donationLng: pickupLng,
      includeTeamIds: existingRecipientTeamIds,
      includeSelf: false,
      includeOnboarding: true,
      isDonationCreation: !isFoodRun,
      ignoreTeamId: ignoreNpoTeamId,
      otherTeamIdToCheck: selectedDonor,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user,
    ignoreNpoTeamId,
    selectedDonor,
    pickupLat,
    pickupLng,
    existingRecipientTeamIdsKey,
  ]);

  const {
    donors,
    recipients: recipientsForFoodRun,
    invalidateMyOrgs,
    fetchRecipientMatches,
    recipientMatchesLoading,
  } = useOrgsForFoodRunCreation({
    lat: user.lat,
    lng: user.lng,
    existingDonorTeamId: recurringPatternData.donor_team_id,
    existingRecipientTeamId: recurringPatternData.receiver_team_id,
    ignoreDonorTeamId,
    ignoreNpoTeamId,
  });

  const recipients = useMemo(
    () =>
      recurringPatternData.is_food_run ? recipientsForFoodRun : nearbyNPOs,
    [recurringPatternData, recipientsForFoodRun, nearbyNPOs]
  );

  useEffect(() => {
    if (selectedDonor && donors.length && recurringPatternData.is_food_run) {
      const selectedDonorTeam = donors.find(
        (item) => item.team_id === selectedDonor
      );
      if (!selectedDonorTeam) return undefined;

      const matchRequest = JSON.parse(recipientMatchRequestKey);
      const request = {
        ...matchRequest,
        lat: matchRequest.lat ?? Number(selectedDonorTeam.lat),
        lng: matchRequest.lng ?? Number(selectedDonorTeam.lng),
        donorTeamId: selectedDonor,
      };
      // Weights are edited on this same screen, so wait for typing to settle
      // rather than re-matching per keystroke.
      const timer = window.setTimeout(() => {
        fetchRecipientMatches(request).catch((error) => {
          console.error('Error matching food run recipients', error);
        });
      }, 800);

      return () => window.clearTimeout(timer);
    }

    return undefined;
  }, [
    donors,
    fetchRecipientMatches,
    recipientMatchRequestKey,
    recurringPatternData.is_food_run,
    selectedDonor,
  ]);

  useEffect(() => {
    if (selectedDonor) {
      invalidateMyOrgs(selectedDonor);
    }
  }, [invalidateMyOrgs, selectedDonor]);

  const invalidate = async () => {
    try {
      await invalidateMyOrgs(selectedDonor);
      if (isFoodRun && recipientMatchRequest) {
        const request = {
          ...recipientMatchRequest,
          donorTeamId: selectedDonor,
        };
        if (request.lat === null || request.lng === null) return;
        await fetchRecipientMatches(request);
      }
    } catch (error) {
      console.error('Error refreshing food run recipients', error);
    }
  };

  return {
    donors,
    recipients,
    invalidate,
    recipientMatchesLoading,
  };
};

export default useEditRecurringDonations;
